import React, { Fragment } from 'react';
import MuiAlert from '@material-ui/lab/Alert';
import { Switch, TextField, Grid, Tooltip } from '@material-ui/core';
import { HelpOutline } from '@material-ui/icons';


const defaultApiSettings = (type) => { 
  return {
    anonymizeMeetings: false,
    anonymizeMembers: false,
    disableGroups: true,
    disableOnlineMeetings: true,
    disableOrgStructure: false,
    apiUserFilter: "", // Either name of group, or object ID of the group
    filteringRules: {
      duration: 480,
      eventType: "workingLocation", // WTF is this?
      domainAlias: "",
      showAs: "oof,free,workingElsewhere"
    },
    agendaCleanerRules: {
      teams: type === "microsoft" ? true : false,
      zoom: type === "microsoft" ? true : false,
      custom: false
    },
    orgStructureField: {
      field: type === "microsoft" ? "department" : "orgUnitPath", // For Outlook // orgUnitPath for Google
      regex: "",
      findAndReplace: "",
      orgSelector: ""
    },
  }
}

const getCurrentConfiguration = (apiSettings = {}, type) => {
  const merged = defaultApiSettings(type);
  Object.keys(apiSettings).forEach(key => {
    if(typeof apiSettings[key] === "object" && merged[key] !== undefined) 
      Object.keys(apiSettings[key]).forEach(nestedKey => {
        if(merged[key][nestedKey] !== undefined)
          merged[key][nestedKey] = apiSettings[key][nestedKey];
      })
    else if(merged[key] !== undefined)
      merged[key] = apiSettings[key];
  })
  return merged;
}

const createAlert = (type, message) => {
  return (
    <MuiAlert key="alertMessage" severity={type} className="p-0">
      {message}
    </MuiAlert>
  );
};

const getExplanation = (name) => {
  const getTooltip = (text) => <Tooltip title={text} className="mr-2"><span><HelpOutline className="mr-3" fontSize="small"/>{name} </span></Tooltip>
  switch(name) {
    case "anonymizeMeetings":
      return getTooltip("Anonymize all meeting subjects at the API fetch stage (privacy filter)");
    case "anonymizeMembers":
      return getTooltip("Anonymize people directory personal details via API configuration (privacy filter)");
    case "disableGroups":
      return getTooltip("Disable group resolution at the API fetch stage");
    case "disableOnlineMeetings":
      return getTooltip("Disable Outlook online meeting at the API fetch stage - Requires a client side policy update via PowerShell scripts before this can be enabled. Contact support to enable this for your account.");
    case "disableOrgStructure":
      return getTooltip("Disable org structure resolution at the API fetch stage (privacy filter)");
    case "apiUserFilter":
      return getTooltip("Filter users by group name or object ID (user data filter) - Example: \"flowtrace-pilot-users@company.domain\"");
    case "filteringRules.duration":
      return getTooltip("Meeting duration in minutes filter at the API fetch stage (data filter)");
    case "filteringRules.eventType":
      return getTooltip("Event type (data filter) *Google Workspace only");
    case "filteringRules.domainAlias":
      return getTooltip("Domain alias to resolve users as (data filter). Example: 'domain.co.uk' would match users from the main domain (e.g. 'domain.com') to 'domain.co.uk' alias and vice versa during the API fetch so meeting's internal participation counting works as expected.");
    case "filteringRules.showAs":
      return getTooltip("Show as filtering at the API fetch stage (data filter) *Outlook Only");
    case "orgStructureField.field":
      return getTooltip("Org structure field to use at the API fetch stage (supports multi-field construct) - Example: \"department,country\" - Defaults: Outlook: 'department', Google Workspace: 'orgUnitPath'");
    case "orgStructureField.regex":
      return getTooltip("Applies a regex rule to org structure field(s) at the API fetch stage (data filter)");
    case "orgStructureField.findAndReplace":
      return getTooltip("Find and replace rule to org structure field(s) at the API fetch stage (data filter) - Example: { \"ReplaceThis\": \"WithThis\" }");
    case "orgStructureField.orgSelector":
      return getTooltip("Find organization to use for structure field(s) at the API fetch stage (data filter) - Example: { \"field\": \"name\", \"value\":\"Flowtrace\" } *Google Workspace Only");
    case "agendaCleanerRules.teams":
      return getTooltip("Use common Teams invite details removal rules when cleaning meeting agendas (data accuracy filter)");
    case "agendaCleanerRules.zoom":
      return getTooltip("Use common Zoom invite details removal rules when cleaning meeting agendas (data accuracy filter)");
    case "agendaCleanerRules.custom":
      return getTooltip("Use custom invite details removal rules when cleaning meeting agendas (data accuracy filter)");
    default:
      return getTooltip("No explanation available for this setting. Please contact support.");
  }
}

const getInGrid = (name, component) => [<Grid item key={`explain-${name}`} xs={4} >{getExplanation(name)}</Grid>,<Grid item key={`value-${name}`} xs={5} >{component}</Grid>, <Grid item key={`filler-${name}`} xs={3} />];

const createEditor = (name, value) => {
  const type = typeof value;
  if(type === "boolean")
    return getInGrid(name, <Switch checked={value} disabled size="small"/>);
  else if(type === "string")
    return getInGrid(name, <TextField value={value} disabled  size="small" fullWidth/>);
  else if(type === "number")
    return getInGrid(name, <TextField value={value} disabled size="small" type="number" />);
    
  return getInGrid(name,value);
}

function ApiFilter(props) {
  const apiConfiguration = props.flowtraceUser && props.flowtraceUser.account && props.flowtraceUser.account.props ? props.flowtraceUser.account.props.apiSettings : {};
  const apiSettings = getCurrentConfiguration(apiConfiguration, props.type);
  const calendarName = props.type === "microsoft" ? "Outlook Calendar" : "Google Calendar";
  const infoAboutSettings = createAlert("info", `These settings control how ${calendarName} is being imported to Flowtrace. Please contact your account manager for discussion about changing them to your needs.`);
  return (
    <Fragment>
      {infoAboutSettings}
      <Grid container spacing={0} alignItems="center" className="mt-2">
      { Object.keys(apiSettings).map(key => {
        if(typeof apiSettings[key] === "object")
          return Object.keys(apiSettings[key]).map(nestedKey => {
            return createEditor(`${key}.${nestedKey}`, apiSettings[key][nestedKey]);
          })
        else
          return createEditor(`${key}`, apiSettings[key]);
      })}
      </Grid>
    </Fragment>
  )


}


export default ApiFilter